import React from "react";
import Section from "./Section";
import img1 from "../../img/25361.jpg";

export default function HTML_PictureElement() {
  return (
    <div className="tutorial-container">
      {/* 🔹 Title */}
      <h1 className="tutorial-title">📸 HTML &lt;picture&gt; Element</h1>

      {/* 🔹 Introduction */}
      <p className="tutorial-intro">
        The <code className="highlight_code_1">&lt;picture&gt;</code> element
        allows developers to serve different images depending on screen-size,
        format support, or resolution. It is commonly used for{" "}
        <strong>responsive images</strong>, <strong>modern formats</strong>, and{" "}
        <strong>art direction</strong>.
      </p>

      {/* 🔹 Key Points */}
      <div
        className="Key_point_container"
        style={{
          background: "linear-gradient(135deg, #eff6ff, #dbeafe)",
          borderRadius: "12px",
          padding: "1rem",
          marginBottom: "1.5rem",
        }}
      >
        <h3
          style={{
            color: "#1d4ed8",
            fontWeight: 700,
            fontSize: "1.5rem",
            marginBottom: "0.5rem",
          }}
        >
          📜 Key Features of the &lt;picture&gt; Element
        </h3>

        <ul className="tutorial-list-points">
          <li>Lets you serve different images for different screen sizes.</li>
          <li>Supports modern formats like WebP or AVIF with fallback.</li>
          <li>Provides art direction (different crops/layouts for devices).</li>
          <li>Browser picks the most appropriate image automatically.</li>
        </ul>
      </div>

      {/* Section 1 — Basic Picture Element */}
      <Section
        title="1️⃣ Basic &lt;picture&gt; Element"
        html={`<picture>
  <source srcset="${img1}" type="image/jpeg" />
  <img src="${img1}" 
    alt="Basic Picture Example" style="
    height:300px; 
    width:100%;
  " />
</picture>`}
        description="The browser selects the first supported <source>; otherwise it loads the <img> fallback."
      />

      {/* Section 2 — Modern Format (WebP) with Fallback */}
      <Section
        title="2️⃣ WebP With Fallback (Modern Formats)"
        html={`<picture>
  <source srcset="${img1}" type="image/webp" />
  <source srcset="${img1}" type="image/jpeg" />
    <img src="${img1}" 
    alt="Basic Picture Example" style="
    height:300px; 
    width:100%;
  " />
</picture>`}
        description="If the browser supports WebP, it loads it; otherwise, it falls back to JPG."
      />

      {/* Section 3 — Responsive Based on Screen Width */}
      <Section
        title="3️⃣ Responsive Images (Different for Mobile, Tablet, Desktop)"
        html={`<picture>
  <source media="(max-width: 600px)" srcset="${img1}" />
  <source media="(max-width: 1200px)" srcset="${img1}" />
  <img src="${img1}" alt="Responsive Example" style="
    height:300px; 
    width:100%;
  " />
</picture>`}
        description="Use media attributes to load different images for mobile, tablet, and desktop."
      />

      {/* Section 4 — Art Direction Example */}
      <Section
        title="4️⃣ Art Direction — Different Crops for Devices"
        html={`<picture>
  <source media="(max-width: 600px)" srcset="mobile-crop.jpg" />
  <source media="(min-width: 601px)" srcset="${img1}" />
    <img src="${img1}" 
    alt="Basic Picture Example" style="
    height:300px; 
    width:100%;
  " />
</picture>`}
        description="Use the <picture> element to show different crops or layouts for mobile vs desktop."
      />

      {/* Section 5 — React Example with Imported Images */}
      <Section
        title="5️⃣ Using &lt;picture&gt; Element in React"
        html={`<picture>
  <source media="(max-width: 600px)" srcSet={mobileImg} />
  <source media="(max-width: 1200px)" srcSet={tabletImg} />
    <img src="${img1}" 
    alt="Basic Picture Example" style="
    height:300px; 
    width:100%;
  " />
</picture>`}
        description="React uses srcSet (not srcset) and camelCase attributes like HTMLImageElement in JSX."
      />

      {/* Summary */}
      <div
        style={{
          background: "linear-gradient(135deg, #fef9c3, #fde68a)",
          padding: "1rem",
          borderRadius: "12px",
          marginTop: "2rem",
        }}
      >
        <h2>📘 Summary — HTML Picture Element</h2>
        <ul className="tutorial-list-points">
          <li>
            <strong>&lt;picture&gt;</strong> enables responsive and optimized
            images.
          </li>
          <li>
            Use <strong>&lt;source&gt;</strong> to specify multiple image
            options.
          </li>
          <li>
            The <strong>&lt;img&gt;</strong> tag acts as the required fallback.
          </li>
          <li>
            Perfect for performance, art direction, and device-specific images.
          </li>
        </ul>
      </div>
    </div>
  );
}
